import { FC, ChangeEvent } from "react";
import styles from "./DatePicker.module.css";

const { DatePickerInputWrapper } = styles;

interface DatePickerProps {
  id: string;
  name: string;
  value?: string | undefined;
  disabled?: boolean;
  placeholder?: string;
  min?: string;
  max?: string;
  handleChange: (dateValue: string) => void;
  "data-testid"?: string;
}

// Convert MM/DD/YYYY to YYYY-MM-DD for HTML date input
const formatToHtmlDate = (value: string | undefined): string => {
  if (!value) return "";

  // Check if already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  // Parse MM/DD/YYYY format
  const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return "";

  const [, month, day, year] = match;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

// Convert YYYY-MM-DD to MM/DD/YYYY for API
const formatToApiDate = (value: string): string => {
  if (!value) return "";

  // Check if already in MM/DD/YYYY format
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) {
    return value;
  }

  // Parse YYYY-MM-DD format
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "";

  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
};

export const DatePicker: FC<DatePickerProps> = ({
  id,
  name,
  value,
  handleChange,
  disabled = false,
  min,
  max,
  "data-testid": dataTestId,
}) => {
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const htmlDateValue = e.target.value;
    const apiDateValue = formatToApiDate(htmlDateValue);
    handleChange(apiDateValue);
  };

  return (
    <div className={DatePickerInputWrapper}>
      <input
        type="date"
        id={id}
        name={name}
        value={formatToHtmlDate(value)}
        onChange={handleInputChange}
        disabled={disabled}
        min={min ? formatToHtmlDate(min) : undefined}
        max={max ? formatToHtmlDate(max) : undefined}
        data-testid={dataTestId}
        autoComplete="off"
      />
    </div>
  );
};
